import React, { memo, useCallback, useState } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import DrawerCDS from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import { loanConditionApiV2 } from 'app/services/api';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { ConditionsTestIds } from '../../Conditions.testIds';

import HistoryEntry from './components/HistoryEntry';
import { wrapperStyles } from './ConditionHistoryDrawer.styles';

const ConditionHistoryDrawer = ({ open, conditionId, conditionTitle, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const fetchHistory = useCallback(() => {
    if (!open || !conditionId) {
      return;
    }

    setIsLoading(true);
    setHasError(false);

    loanConditionApiV2
      .getConditionHistory({ loanConditionId: conditionId })
      .then(({ data }) => setEntries(data ?? []))
      .catch(() => setHasError(true))
      .finally(() => setIsLoading(false));
  }, [open, conditionId]);

  useEffectSafeStrictMode(() => {
    fetchHistory();
  }, [fetchHistory]);

  const drawerContent = (
    <Box sx={wrapperStyles}>
      {isLoading && <CircularProgress size={32} />}

      {!isLoading && hasError && (
        <Typography color="error.main" variant="regular14">
          Failed to load history. Please try again.
        </Typography>
      )}

      {!isLoading && !hasError && !entries.length && (
        <Typography color="text.secondary" variant="regular14">
          No history available for this condition.
        </Typography>
      )}

      {!isLoading &&
        !hasError &&
        entries.map(entry => (
          <HistoryEntry entry={entry} key={entry.loanConditionHistoryEntryId} />
        ))}
    </Box>
  );

  return (
    <DrawerCDS
      content={drawerContent}
      data-testid={ConditionsTestIds.HISTORY_DRAWER}
      open={open}
      title={conditionTitle ? `History — ${conditionTitle}` : 'Condition History'}
      onClose={onClose}
    />
  );
};

ConditionHistoryDrawer.propTypes = {
  conditionId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  conditionTitle: PropTypes.string,
};

export default memo(ConditionHistoryDrawer);
